// CreditContext.js with better error handling and state management
// src/context/CreditContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

// Create the Credit context
const CreditContext = createContext();

// Custom hook for using the Credit context
export const useCredits = () => {
  return useContext(CreditContext);
};

// Provider component that wraps the app to provide credit information
export const CreditProvider = ({ children }) => {
  const [credits, setCredits] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch authentication status and credits on component mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Primary endpoint for auth status with credits
        const response = await fetch('/api/credits/auth-status-with-credits/', {
          credentials: 'include',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('Auth status with credits response:', data);
          
          setIsAuthenticated(data.isAuthenticated);
          
          // Check if user property exists and has credits
          if (data.isAuthenticated && data.user && data.user.credits !== undefined) {
            console.log('Setting credits to:', data.user.credits);
            setCredits(parseInt(data.user.credits, 10) || 0);
          } else if (data.isAuthenticated && data.credits !== undefined) {
            // Alternative response format
            console.log('Setting credits from top level:', data.credits);
            setCredits(parseInt(data.credits, 10) || 0);
          } else {
            // If authenticated but no credits found in the response
            console.log('No credits in response, fetching separately');
            fetchCreditsOnly();
          }
        } else {
          console.log('Primary endpoint failed, trying fallback');
          // Fall back to separate endpoints
          const authResponse = await fetch('/api/auth-status/', {
            credentials: 'include',
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });
          
          if (authResponse.ok) {
            const authData = await authResponse.json();
            console.log('Auth status response:', authData);
            setIsAuthenticated(authData.isAuthenticated);
            
            if (authData.isAuthenticated) {
              fetchCreditsOnly();
            }
          } else {
            throw new Error('Failed to fetch authentication status');
          }
        }
      } catch (error) {
        console.error('Error in credit context:', error);
        setError(error.message);
        setIsAuthenticated(false);
        setCredits(0);
      } finally {
        setIsLoading(false);
      }
    };
    
    const fetchCreditsOnly = async () => {
      try {
        const creditResponse = await fetch('/api/credits/user-credits/', {
          credentials: 'include',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (creditResponse.ok) {
          const creditData = await creditResponse.json();
          console.log('Credit-only response:', creditData);
          
          if (creditData.success && creditData.credits !== undefined) {
            console.log('Setting credits to:', creditData.credits);
            setCredits(parseInt(creditData.credits, 10) || 0);
          } else {
            console.warn('Unexpected credit response format:', creditData);
          }
        } else {
          console.warn('Failed to fetch credits', creditResponse.status);
        }
      } catch (e) {
        console.error('Error fetching credits only:', e);
      }
    };

    checkAuthStatus();
    
    // Set up periodic refresh of credit balance (every 5 minutes)
    const refreshInterval = setInterval(() => {
      if (isAuthenticated) {
        fetchCreditsOnly();
      }
    }, 5 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, []);

  // Function to manually refresh credits
  const refreshCredits = async () => {
    if (isAuthenticated) {
      setIsLoading(true);
      try {
        const response = await fetch('/api/credits/user-credits/', {
          credentials: 'include',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.credits !== undefined) {
            setCredits(parseInt(data.credits, 10) || 0);
          }
        }
      } catch (error) {
        console.error('Error refreshing credits:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Provided context value
  const value = {
    credits,
    isAuthenticated,
    isLoading,
    error,
    showLoginModal,
    showCreditPurchaseModal,
    setShowLoginModal,
    setShowCreditPurchaseModal,
    setCredits,
    refreshCredits
  };

  return (
    <CreditContext.Provider value={value}>
      {children}
    </CreditContext.Provider>
  );
};

export default CreditProvider;

// // src/context/CreditContext.js
// import React, { createContext, useContext, useState, useEffect } from 'react';

// // Create the Credit context
// const CreditContext = createContext();

// // Custom hook for using the Credit context
// export const useCredits = () => {
//   return useContext(CreditContext);
// };

// // Provider component that wraps the app to provide credit information
// export const CreditProvider = ({ children }) => {
//   const [credits, setCredits] = useState(0);
//   const [isAuthenticated, setIsAuthenticated] = useState(false);
//   const [showLoginModal, setShowLoginModal] = useState(false);
//   const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
//   const [isLoading, setIsLoading] = useState(true);

//   // Fetch authentication status and credits on component mount
//   useEffect(() => {
//     const checkAuthStatus = async () => {
//       setIsLoading(true);
//       try {
//         // Use the correct API endpoint URL based on your Django URL configuration
//         // The Django URL is registered as /api/auth-status-with-credits/ in your credits URLs
//         const response = await fetch('/api/auth-status-with-credits/', {
//           credentials: 'include' // Important for sending cookies with the request
//         });
        
//         if (!response.ok) {
//           // Fall back to the standard auth endpoint
//           const fallbackResponse = await fetch('/api/auth-status/', {
//             credentials: 'include'
//           });
          
//           if (!fallbackResponse.ok) {
//             // If both fail, assume not authenticated
//             setIsAuthenticated(false);
//             setCredits(0);
//             setIsLoading(false);
//             return;
//           }
          
//           const fallbackData = await fallbackResponse.json();
//           setIsAuthenticated(fallbackData.isAuthenticated);
          
//           // If authenticated, try to fetch credits separately
//           if (fallbackData.isAuthenticated) {
//             try {
//               const creditResponse = await fetch('/api/user-credits/', {
//                 credentials: 'include'
//               });
              
//               if (creditResponse.ok) {
//                 const creditData = await creditResponse.json();
//                 setCredits(creditData.credits);
//               }
//             } catch (e) {
//               console.error('Error fetching credits:', e);
//             }
//           }
//         } else {
//           // Primary endpoint worked - parse the combined auth+credits response
//           const data = await response.json();
//           setIsAuthenticated(data.isAuthenticated);
//           if (data.isAuthenticated) {
//             setCredits(data.credits);
//           }
//         }
//       } catch (error) {
//         console.error('Error checking auth status:', error);
//         // Try the fallback approach on error
//         try {
//           const fallbackResponse = await fetch('/api/auth-status/', {
//             credentials: 'include'
//           });
//           const fallbackData = await fallbackResponse.json();
//           setIsAuthenticated(fallbackData.isAuthenticated);
//           setCredits(0); // Default to 0 credits if we can't fetch them
//         } catch (e) {
//           console.error('Error with fallback auth check:', e);
//           setIsAuthenticated(false);
//         }
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     checkAuthStatus();
//   }, []);

//   // Provided context value
//   const value = {
//     credits,
//     isAuthenticated,
//     isLoading,
//     showLoginModal,
//     showCreditPurchaseModal,
//     setShowLoginModal,
//     setShowCreditPurchaseModal,
//     setCredits
//   };

//   return (
//     <CreditContext.Provider value={value}>
//       {children}
//     </CreditContext.Provider>
//   );
// };